/*
 * PART — деталь на сцене мультика.
 *
 * ЗАЧЕМ ЗАМЕНИЛ CSS-КУБИК. Раньше объект собирался из трёх div-граней, и на
 * нём физически нельзя было показать то, о чём урок: отверстие, паз,
 * скругление, полость. В каждом мультике появлялся один и тот же кубик,
 * и урок про пазы выглядел ровно как урок про оболочку.
 *
 * Здесь деталь рисуется одним SVG в изометрии, и на ней можно показать
 * ПРИЗНАКИ — то, что ученик и должен увидеть:
 *   holes    — отверстия на верхней грани (число и диаметр)
 *   slot     — паз (вытянутое отверстие)
 *   fillet   — скруглённые вертикальные рёбра
 *   chamfer  — фаска по верхнему контуру
 *   shell    — полость (оболочка), видна открытая грань
 *   posts    — стойки на дне
 *   window   — окно-вырез в верхней грани
 *   text     — выпуклые буквы
 *
 * Признаки НАКАПЛИВАЮТСЯ по ходу урока: сначала пластина, потом на ней
 * скругления, потом отверстия. Это и есть «модель, которую создаёшь»,
 * а не готовая картинка.
 *
 * ИЗОМЕТРИЯ здесь та же, что во всём проекте: x вправо-вглубь, y влево-вглубь,
 * z вверх. Деталь описывается габаритами в миллиметрах, масштаб под кадр
 * подбирается автоматически.
 */

import React from 'react';

/** Проекция точки детали (x, y, z) на экран. */
function iso(x, y, z) {
    return [(x - y) * 0.866, (x + y) * 0.5 - z];
}

/** Цвета граней: верх светлее, бока темнее — так читается объём. */
const FACE = {
    top: '#b9c3d1',
    left: '#8d97a7',
    right: '#a3adbb',
    line: '#59616f',
    holeIn: '#2a3038',
    accent: '#2f86d6',
    hint: '#3ecf8e',
};


/**
 * Деталь.
 *
 * @param {Object} p
 *   w, d, h  — габариты в миллиметрах (длина, глубина, высота)
 *   holes    — [{ x, y, r }] отверстия на верхней грани, координаты от центра
 *   slot     — { x, y, len, w } паз
 *   fillet   — радиус скругления вертикальных рёбер (мм)
 *   chamfer  — размер фаски по верху (мм)
 *   shell    — толщина стенки: деталь становится полой, верх открыт
 *   posts    — [{ x, y, r, h }] стойки на дне
 *   window   — { w, d } окно-вырез в верхней грани
 *   text     — надпись выпуклыми буквами
 *   cut      — { w, d, depth } углубление в верхней грани
 */
export default function Part({
    w = 40, d = 30, h = 20,
    holes, slot, fillet, chamfer, shell, posts, windowCut, text, cut,
    /* Формы курса 4 «Тела вращения»: у них другая геометрия, а не признак
       поверх бруска. shape: 'cone' | 'cylinder' | 'shaft' | 'gear' | 'spring' */
    shape, teeth, coils, wire, profile,
    selected, appear = 1, size = 220,
}) {
    /* Круглые тела рисуются отдельной веткой: у них нет граней бруска. */
    if (shape) {
        return (
            <RoundPart shape={shape} w={w} h={h} teeth={teeth} coils={coils}
                wire={wire} profile={profile} holes={holes}
                selected={selected} appear={appear} size={size}/>
        );
    }
    /* Масштаб: деталь любого размера должна поместиться в кадр. */
    const span = Math.max(w + d, h * 1.6, 1);
    const k = (size * 0.66) / span;

    const P = (x, y, z) => {
        const [sx, sy] = iso(x * k, y * k, z * k);
        return [sx, sy];
    };
    const pt = (x, y, z) => P(x, y, z).join(',');

    const hw = w / 2, hd = d / 2;
    /* Скругление показываем срезом угла — в изометрии этого достаточно,
       чтобы прочитать «угол больше не острый». */
    const f = Math.min(fillet || 0, Math.min(hw, hd) * 0.8);

    /** Контур верхней грани с учётом скруглений. */
    const topOutline = () => {
        if (!f) {
            return `${pt(-hw, -hd, h)} ${pt(hw, -hd, h)} `
                + `${pt(hw, hd, h)} ${pt(-hw, hd, h)}`;
        }
        return [
            pt(-hw + f, -hd, h), pt(hw - f, -hd, h),
            pt(hw, -hd + f, h), pt(hw, hd - f, h),
            pt(hw - f, hd, h), pt(-hw + f, hd, h),
            pt(-hw, hd - f, h), pt(-hw, -hd + f, h),
        ].join(' ');
    };

    /* Толщина стенки для оболочки — внутренний контур. */
    const t = shell || 0;

    return (
        <g opacity={appear} style={{ transition: 'opacity .2s' }}>
            {/* ── Боковые грани ── */}
            <polygon points={`${pt(-hw, hd, h)} ${pt(hw, hd, h)} `
                + `${pt(hw, hd, 0)} ${pt(-hw, hd, 0)}`}
                fill={FACE.left} stroke={FACE.line} strokeWidth="1.1"/>
            <polygon points={`${pt(hw, -hd, h)} ${pt(hw, hd, h)} `
                + `${pt(hw, hd, 0)} ${pt(hw, -hd, 0)}`}
                fill={FACE.right} stroke={FACE.line} strokeWidth="1.1"/>

            {/* ── Верхняя грань ── */}
            <polygon points={topOutline()}
                fill={FACE.top} stroke={FACE.line} strokeWidth="1.1"
                strokeLinejoin="round"/>

            {/* ── Фаска: узкая полоска вдоль верхнего контура ── */}
            {chamfer > 0 && (
                <polygon points={`${pt(-hw, -hd, h)} ${pt(hw, -hd, h)} `
                    + `${pt(hw - chamfer, -hd + chamfer, h)} `
                    + `${pt(-hw + chamfer, -hd + chamfer, h)}`}
                    fill="#c9d2de" stroke={FACE.line} strokeWidth="0.8"/>
            )}

            {/* ── Оболочка: открытая верхняя грань и стенки ── */}
            {t > 0 && (
                <>
                    <polygon points={`${pt(-hw + t, -hd + t, h - t)} `
                        + `${pt(hw - t, -hd + t, h - t)} `
                        + `${pt(hw - t, hd - t, h - t)} `
                        + `${pt(-hw + t, hd - t, h - t)}`}
                        fill={FACE.holeIn} stroke={FACE.line} strokeWidth="0.9"/>
                    <polygon points={`${pt(-hw + t, -hd + t, h)} `
                        + `${pt(hw - t, -hd + t, h)} `
                        + `${pt(hw - t, hd - t, h)} `
                        + `${pt(-hw + t, hd - t, h)}`}
                        fill="none" stroke={FACE.hint} strokeWidth="1.4"/>
                </>
            )}

            {/* ── Углубление в верхней грани ── */}
            {cut && (
                <>
                    <polygon points={`${pt(-cut.w / 2, -cut.d / 2, h - cut.depth)} `
                        + `${pt(cut.w / 2, -cut.d / 2, h - cut.depth)} `
                        + `${pt(cut.w / 2, cut.d / 2, h - cut.depth)} `
                        + `${pt(-cut.w / 2, cut.d / 2, h - cut.depth)}`}
                        fill={FACE.holeIn} stroke={FACE.line} strokeWidth="0.9"/>
                    <polygon points={`${pt(-cut.w / 2, -cut.d / 2, h)} `
                        + `${pt(cut.w / 2, -cut.d / 2, h)} `
                        + `${pt(cut.w / 2, cut.d / 2, h)} `
                        + `${pt(-cut.w / 2, cut.d / 2, h)}`}
                        fill="none" stroke={FACE.hint} strokeWidth="1.2"/>
                </>
            )}

            {/* ── Окно-вырез насквозь ── */}
            {windowCut && (
                <polygon points={`${pt(-windowCut.w / 2, -windowCut.d / 2, h)} `
                    + `${pt(windowCut.w / 2, -windowCut.d / 2, h)} `
                    + `${pt(windowCut.w / 2, windowCut.d / 2, h)} `
                    + `${pt(-windowCut.w / 2, windowCut.d / 2, h)}`}
                    fill={FACE.holeIn} stroke={FACE.hint} strokeWidth="1.3"/>
            )}

            {/* ── Отверстия на верхней грани ── */}
            {(holes || []).map((o, i) => {
                const [cx, cy] = P(o.x || 0, o.y || 0, h);
                /* Минимальный экранный радиус: отверстие под М3 на большой
                   пластине иначе схлопывается в точку и его не видно. */
                const r = Math.max((o.r || 3) * k, 4);
                return (
                    <ellipse key={i} cx={cx} cy={cy} rx={r * 0.866} ry={r * 0.5}
                        fill={FACE.holeIn} stroke={FACE.line} strokeWidth="0.9"/>
                );
            })}

            {/* ── Паз: прямоугольник с полукруглыми концами ── */}
            {slot && (() => {
                const half = (slot.len - slot.w) / 2;
                const [ax, ay] = P((slot.x || 0) - half, slot.y || 0, h);
                const [bx, by] = P((slot.x || 0) + half, slot.y || 0, h);
                const rx = (slot.w / 2) * k * 0.866;
                const ry = (slot.w / 2) * k * 0.5;
                return (
                    <g>
                        <path d={`M${ax},${ay - ry} L${bx},${by - ry} `
                            + `A${rx},${ry} 0 0 1 ${bx},${by + ry} `
                            + `L${ax},${ay + ry} `
                            + `A${rx},${ry} 0 0 1 ${ax},${ay - ry} Z`}
                            fill={FACE.holeIn} stroke={FACE.line} strokeWidth="0.9"/>
                    </g>
                );
            })()}

            {/* ── Стойки на дне ── */}
            {/* Стойки рисуем ПОСЛЕ окна: если они попадают под вырез,
                видно, что стойка стоит на дне, а не висит в проёме. */}
            {(posts || []).map((p, i) => {
                const [bx, by] = P(p.x || 0, p.y || 0, t || 0);
                const [tx, ty] = P(p.x || 0, p.y || 0, (t || 0) + (p.h || 6));
                const r = (p.r || 4) * k;
                return (
                    <g key={i}>
                        <path d={`M${bx - r * 0.866},${by} L${tx - r * 0.866},${ty} `
                            + `A${r * 0.866},${r * 0.5} 0 0 0 ${tx + r * 0.866},${ty} `
                            + `L${bx + r * 0.866},${by} `
                            + `A${r * 0.866},${r * 0.5} 0 0 1 ${bx - r * 0.866},${by} Z`}
                            fill={FACE.right} stroke={FACE.line} strokeWidth="0.8"/>
                        <ellipse cx={tx} cy={ty} rx={r * 0.866} ry={r * 0.5}
                            fill={FACE.top} stroke={FACE.line} strokeWidth="0.8"/>
                        {/* Отверстие под винт в стойке */}
                        <ellipse cx={tx} cy={ty} rx={r * 0.36} ry={r * 0.21}
                            fill={FACE.holeIn}/>
                    </g>
                );
            })}

            {/* ── Выпуклый текст на верхней грани ── */}
            {text && (() => {
                const [tx, ty] = P(0, 0, h);
                return (
                    <text x={tx} y={ty + 3} textAnchor="middle"
                        fontSize={Math.max(9, h * k * 0.5)}
                        fontWeight="700" fill="#6c7686"
                        style={{ letterSpacing: '1px' }}>{text}</text>
                );
            })()}

            {/* ── Скругления: подсветка обработанных рёбер ── */}
            {/* Скругления подсвечиваем зелёными дугами на всех видимых углах:
                срез в контуре читается слабо, а дуга сразу говорит
                «этот угол обработан». */}
            {f > 0 && (
                <g fill="none" stroke={FACE.hint} strokeWidth="2"
                    strokeLinecap="round">
                    {[[hw - f, -hd, hw, -hd + f], [hw - f, hd, hw, hd - f],
                      [-hw + f, hd, -hw, hd - f], [-hw + f, -hd, -hw, -hd + f]]
                        .map(([x1, y1, x2, y2], i) => {
                            const [ax, ay] = P(x1, y1, h);
                            const [bx, by] = P(x2, y2, h);
                            const [cx2, cy2] = P(
                                x1 > 0 ? hw : -hw, y1 > 0 ? hd : -hd, h);
                            return <path key={i}
                                d={`M${ax},${ay} Q${cx2},${cy2} ${bx},${by}`}/>;
                        })}
                </g>
            )}

            {/* ── Рамка выделения ── */}
            {selected && (
                <polygon points={topOutline()} fill="none"
                    stroke={FACE.accent} strokeWidth="2"
                    strokeDasharray="5 3" opacity="0.9"/>
            )}
        </g>
    );
}


/*
 * ── ТЕЛА ВРАЩЕНИЯ И МАССИВЫ (курс 4) ─────────────────────────────────
 *
 * Всё, что получается вращением профиля вокруг оси или размножением по
 * кругу. Рисуем в той же изометрии: круг в плане становится эллипсом
 * с соотношением сторон 0.5 — так же, как верхняя грань бруска.
 */
function RoundPart({ shape, w, h, teeth, coils, wire, profile, holes,
    selected, appear, size }) {

    const span = Math.max(w * 1.4, h * 1.6, 1);
    const k = (size * 0.62) / span;
    const R = (w / 2) * k;         // радиус в экранных единицах
    const H = h * k;               // высота
    const ry = (r) => r * 0.5;     // сжатие эллипса в изометрии

    const stroke = FACE.line;

    /** Боковая поверхность тела вращения между радиусами r1 (низ) и r2 (верх). */
    const side = (r1, r2, y1, y2, fill) => (
        <path d={`M${-r1},${y1} L${-r2},${y2} `
            + `A${r2},${ry(r2)} 0 0 0 ${r2},${y2} `
            + `L${r1},${y1} `
            + `A${r1},${ry(r1)} 0 0 1 ${-r1},${y1} Z`}
            fill={fill} stroke={stroke} strokeWidth="1.1"/>
    );

    /** Эллипс — круг в изометрии. */
    const disc = (r, y, fill) => (
        <ellipse cx="0" cy={y} rx={r} ry={ry(r)}
            fill={fill} stroke={stroke} strokeWidth="1.1"/>
    );

    let body = null;

    if (shape === 'cone') {
        /* Воронка: широкий верх, узкий носик. */
        const rTop = R, rNeck = R * 0.18;
        body = (
            <>
                {side(rNeck, rTop, H * 0.15, -H * 0.55, FACE.right)}
                {side(rNeck, rNeck, H * 0.62, H * 0.15, FACE.left)}
                {disc(rTop, -H * 0.55, FACE.top)}
                {/* Внутренняя воронка — видно, что она полая */}
                <ellipse cx="0" cy={-H * 0.55} rx={rTop * 0.82}
                    ry={ry(rTop * 0.82)} fill={FACE.holeIn}
                    stroke={FACE.hint} strokeWidth="1.3"/>
            </>
        );
    } else if (shape === 'shaft') {
        /* Ступенчатый вал: три цилиндра разного диаметра. */
        const steps = profile || [[1, 0.3], [0.62, 0.4], [0.4, 0.3]];
        let y = H * 0.5;
        body = (
            <>
                {steps.map(([rk, hk], i) => {
                    const r = R * rk, hh = H * hk;
                    const yb = y, yt = y - hh;
                    y = yt;
                    return (
                        <g key={i}>
                            {side(r, r, yb, yt, i % 2 ? FACE.left : FACE.right)}
                            {disc(r, yt, FACE.top)}
                        </g>
                    );
                })}
            </>
        );
    } else if (shape === 'gear') {
        /* Шестерёнка: диск + зубья по кругу. Число зубьев — из урока. */
        const n = Math.max(6, Math.min(teeth || 12, 28));
        const rIn = R * 0.78;
        const tw = (Math.PI * 2) / n * 0.42;   // угловая полуширина зуба
        return (
            <g opacity={appear} style={{ transition: 'opacity .2s' }}>
                {/* Толщина: боковая поверхность диска. Рисуем ПЕРВОЙ,
                    зубья лягут поверх — иначе нижние зубья ею перекроются. */}
                {side(rIn, rIn, H * 0.5, -H * 0.5, FACE.right)}
                {/* Зубья — трапеции по окружности, массив по кругу */}
                {Array.from({ length: n }, (_, i) => {
                    const a = (i / n) * Math.PI * 2;
                    const p = (rad, ang) => `${rad * Math.cos(ang)},`
                        + `${ry(rad) * Math.sin(ang) - H * 0.5}`;
                    return (
                        <polygon key={i}
                            points={`${p(rIn, a - tw)} ${p(R, a - tw * 0.62)} `
                                + `${p(R, a + tw * 0.62)} ${p(rIn, a + tw)}`}
                            fill={FACE.top} stroke={stroke} strokeWidth="0.9"/>
                    );
                })}
                {disc(rIn, -H * 0.5, FACE.top)}
                {/* Отверстие под вал в центре */}
                <ellipse cx="0" cy={-H * 0.5} rx={R * 0.16} ry={ry(R * 0.16)}
                    fill={FACE.holeIn} stroke={stroke} strokeWidth="0.9"/>
                {selected && (
                    <ellipse cx="0" cy={-H * 0.5} rx={R} ry={ry(R)}
                        fill="none" stroke={FACE.accent} strokeWidth="2"
                        strokeDasharray="5 3"/>
                )}
            </g>
        );
    } else if (shape === 'spring') {
        /* Пружина: витки как наложенные эллипсы, снизу вверх. */
        const n = Math.max(3, Math.min(coils || 6, 12));
        const t = (wire || 3) * k;
        const step = H / n;
        return (
            <g opacity={appear} style={{ transition: 'opacity .2s' }}>
                {Array.from({ length: n }, (_, i) => {
                    const y = H * 0.5 - i * step;
                    return (
                        <g key={i}>
                            {/* Дальняя половина витка — тусклее */}
                            <path d={`M${-R},${y} A${R},${ry(R)} 0 0 1 ${R},${y - step * 0.5}`}
                                fill="none" stroke={FACE.left}
                                strokeWidth={t} strokeLinecap="round"/>
                            {/* Ближняя половина — ярче: так виден объём спирали */}
                            <path d={`M${R},${y - step * 0.5} A${R},${ry(R)} 0 0 1 ${-R},${y - step}`}
                                fill="none" stroke={FACE.top}
                                strokeWidth={t} strokeLinecap="round"/>
                        </g>
                    );
                })}
                {selected && (
                    <ellipse cx="0" cy="0" rx={R * 1.15} ry={H * 0.62}
                        fill="none" stroke={FACE.accent} strokeWidth="2"
                        strokeDasharray="5 3"/>
                )}
            </g>
        );
    } else {
        /* Обычный цилиндр. */
        body = (
            <>
                {side(R, R, H * 0.5, -H * 0.5, FACE.right)}
                {disc(R, -H * 0.5, FACE.top)}
            </>
        );
    }

    return (
        <g opacity={appear} style={{ transition: 'opacity .2s' }}>
            {body}
            {/* Отверстия по центру — для валов и воронок */}
            {(holes || []).map((o, i) => (
                <ellipse key={i} cx="0" cy={-H * 0.5}
                    rx={Math.max((o.r || 3) * k, 4)}
                    ry={ry(Math.max((o.r || 3) * k, 4))}
                    fill={FACE.holeIn} stroke={stroke} strokeWidth="0.9"/>
            ))}
            {selected && (
                <ellipse cx="0" cy={-H * 0.5} rx={R} ry={ry(R)}
                    fill="none" stroke={FACE.accent} strokeWidth="2"
                    strokeDasharray="5 3"/>
            )}
        </g>
    );
}
